import { useState } from "react";
import Api from "../../api/api";
import stringHelpers from "../../../utilities/stringHelpers";
import { dateRange } from "../../../utilities/dateUtilities";
import { DateGroupingTypeEnum, VehicleTypeEnum } from "../reportsEnums";
import { ScheduleExportDateRangePicker, isScheduleDateRangeCustomRangeInvalid } from "../scheduleExportDateRangePicker";
import { SearchSelect } from "../../shared/searchSelect";
import { ScheduleExportModal } from "../../shared/scheduleExportModal";

export const averageLengthOfStayReportType = 11

export const AverageLengthOfStayScheduleModal = ({ sites, organisationId, token, onCancel, onSaved, scheduledExport, initialParameters }) => {
    const seedParameters = scheduledExport?.parameters ?? initialParameters ?? {}
    const hasCustomDates = seedParameters.from != null || seedParameters.to != null
    const isEditing = !stringHelpers.isNullOrEmpty(scheduledExport?.id)

    const [scheduleFromDate, setScheduleFromDate] = useState(seedParameters.from ?? dateRange("lastMonth").firstDate)
    const [scheduleToDate, setScheduleToDate] = useState(seedParameters.to ?? dateRange("lastMonth").lastDate)
    const [scheduledDateRangeTypePreset, setScheduledDateRangeTypePreset] = useState(seedParameters.reportDatePreset || (hasCustomDates ? null : 0))
    const [scheduleSelectedSite, setScheduleSelectedSite] = useState(seedParameters.siteId ?? null)
    const [scheduleVehicleType, setScheduleVehicleType] = useState(seedParameters.vehicleType ?? 0)
    const [scheduleSelectedDateGrouping, setScheduleSelectedDateGrouping] = useState(seedParameters.dateRangeType ?? 0)
    const [isSaving, setIsSaving] = useState(false)
    const [saveErrorMessage, setSaveErrorMessage] = useState(null)

    const isScheduleCustomDateRangeInvalid = isScheduleDateRangeCustomRangeInvalid(scheduledDateRangeTypePreset, scheduleFromDate, scheduleToDate)
    const isSubmitDisabled = isScheduleCustomDateRangeInvalid || isSaving

    const buildScheduledExportParameters = () => {
        const isUsingDateRangePreset = scheduledDateRangeTypePreset !== null
        const dateRangeParameters = isUsingDateRangePreset
            ? { reportDatePreset: scheduledDateRangeTypePreset }
            : { from: scheduleFromDate, to: scheduleToDate }

        return {
            ...dateRangeParameters,
            siteId: scheduleSelectedSite,
            vehicleType: scheduleVehicleType,
            dateRangeType: scheduleSelectedDateGrouping,
        }
    }

    const handleSaveFailed = (error) => {
        setSaveErrorMessage(error?.errorMessage ?? "Unable to save scheduled export")
        setIsSaving(false)
    }

    const handleSaveSucceeded = (data) => {
        if (data?.isError) {
            handleSaveFailed(data)
        } else {
            onSaved()
        }
    }

    return (
        <ScheduleExportModal
            initialValues={ scheduledExport }
            reportType={ averageLengthOfStayReportType }
            onCancel={ onCancel }
            disableConfirm={ isSubmitDisabled }
            errorMessage={ saveErrorMessage }
            onSubmit={ (isActive, name, scheduleFrequencyMode, monday, tuesday, wednesday, thursday, friday, saturday, sunday, dayOfMonth, time, recipients) => {
                setIsSaving(true)
                setSaveErrorMessage(null)
                if (isEditing) {
                    Api.updateScheduledExport(
                        handleSaveSucceeded,
                        handleSaveFailed,
                        scheduledExport.id,
                        name, averageLengthOfStayReportType, scheduleFrequencyMode,
                        monday,
                        tuesday,
                        wednesday,
                        thursday,
                        friday,
                        saturday,
                        sunday,
                        time,
                        dayOfMonth,
                        recipients,
                        buildScheduledExportParameters(),
                        isActive,
                        organisationId, token
                    )
                } else {
                    Api.createScheduledExport(
                        handleSaveSucceeded,
                        handleSaveFailed,
                        name, averageLengthOfStayReportType, scheduleFrequencyMode,
                        monday,
                        tuesday,
                        wednesday,
                        thursday,
                        friday,
                        saturday,
                        sunday,
                        time,
                        dayOfMonth,
                        recipients,
                        buildScheduledExportParameters(),
                        isActive,
                        organisationId, token
                    )
                }
            } }
        >
            <div className="">
                <h5 className="mb-2">Filters</h5>
                <div className="row mb-2">
                    <div className="col-md-3">Date Range</div>
                    <div className="col-md-9">
                        <ScheduleExportDateRangePicker
                            scheduledDateRangeTypePreset={ scheduledDateRangeTypePreset }
                            setScheduledDateRangeTypePreset={ setScheduledDateRangeTypePreset }
                            scheduleFromDate={ scheduleFromDate }
                            setScheduleFromDate={ setScheduleFromDate }
                            scheduleToDate={ scheduleToDate }
                            setScheduleToDate={ setScheduleToDate }
                        />
                    </div>
                </div>
                <div className="row mb-2">
                    <div className="col-md-3">Site</div>
                    <div className="col-md-9">
                        <SearchSelect
                            width="100%"
                            options={ sites.map(item => ({ value: item.id, label: item.name })) }
                            onSelect={ value => setScheduleSelectedSite(value) }
                            selected={ scheduleSelectedSite }
                        />
                    </div>
                </div>
                <div className="row mb-2">
                    <div className="col-md-3">Vehicle Type</div>
                    <div className="col-md-9">
                        <div className="options-selector-container">
                            <select
                                id="vehicle-type-selector"
                                className="border rounded py-1 px-2 w-100"
                                onChange={ (e) => setScheduleVehicleType(parseInt(e.target.value)) }
                                value={ scheduleVehicleType }
                            >
                                { Object.entries(VehicleTypeEnum).map(([key, value]) => <option key={ key } value={ key }>{ value }</option>) }
                            </select>
                        </div>
                    </div>
                </div>
                <div className="row mb-2">
                    <div className="col-md-3">Date Grouping</div>
                    <div className="col-md-9">
                        <div className="options-selector-container">
                            <select
                                id="date-grouping-selector"
                                className="border rounded py-1 px-2 w-100"
                                onChange={ (e) => setScheduleSelectedDateGrouping(parseInt(e.target.value)) }
                                value={ scheduleSelectedDateGrouping }
                            >
                                { Object.entries(DateGroupingTypeEnum).map(([key, value]) => <option key={ key } value={ key }>{ value }</option>) }
                            </select>
                        </div>
                    </div>
                </div>
            </div>
            <hr className="border-dashed border-bottom-0"></hr>
        </ScheduleExportModal>
    )
}
